/** Hotel mutations: create a hotel. NON-OPTIMISTIC. */

import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { unwrap } from '../queries/_helpers';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import { type Hotel, hotelSchema } from '../responses';
import type { CreateHotelBody } from './_types';

/** `POST /hotels` — create a hotel under the tenant. */
export function useCreateHotel(
  tenantId: string,
): UseMutationResult<Hotel, unknown, CreateHotelBody> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateHotelBody) => {
      const result = await client.POST('/api/v1/tenants/{tenantId}/hotels', {
        params: { path: { tenantId } },
        body,
        parseAs: 'json',
      });
      return unwrap(result, hotelSchema);
    },
    onSuccess: (hotel) => {
      queryClient.setQueryData(queryKeys.hotel(tenantId, hotel.id), hotel);
      void queryClient.invalidateQueries({ queryKey: queryKeys.hotels(tenantId) });
    },
  });
}

/** Editable hotel fields (slug is immutable). */
export interface UpdateHotelBody {
  name?: string;
  timezone?: string;
  defaultLanguage?: string;
  languages?: string[];
  status?: 'draft' | 'active' | 'suspended';
}

/** `PATCH /hotels/:hotelId` — update mutable hotel fields. */
export function useUpdateHotel(
  tenantId: string,
  hotelId: string,
): UseMutationResult<Hotel, unknown, UpdateHotelBody> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateHotelBody) => {
      const result = await client.PATCH('/api/v1/tenants/{tenantId}/hotels/{hotelId}', {
        params: { path: { tenantId, hotelId } },
        body,
        parseAs: 'json',
      });
      return unwrap(result, hotelSchema);
    },
    onSuccess: (hotel) => {
      queryClient.setQueryData(queryKeys.hotel(tenantId, hotelId), hotel);
      void queryClient.invalidateQueries({ queryKey: queryKeys.hotels(tenantId) });
    },
  });
}
